/**
 * Guide photo types for "How to find" — fixed slots:
 * streetView → {num}_1.jpg, entrance → {num}_2.jpg, landmark → {num}_3.jpg
 */
export const GUIDE_PHOTO_TYPES = ['streetView', 'entrance', 'landmark'];

/** 1-based S3 / legacy index for a type. */
export function guidePhotoIndex(type) {
  const i = GUIDE_PHOTO_TYPES.indexOf(type);
  return i >= 0 ? i + 1 : null;
}

export function getDefaultGuidePhotoSlots() {
  return GUIDE_PHOTO_TYPES.map((type, index) => ({
    type,
    id: `slot-${type}-${index}`,
    file: null,
    previewUrl: null,
    existingUrl: null,
  }));
}

/**
 * Map API response (typed objects or legacy string URLs) to 3 slots.
 * Typed: [{ type, url }] — preferred.
 * Legacy string[]: index 0→streetView, 1→entrance, 2→landmark.
 */
export function mapUrlsToGuidePhotoSlots(urlsOrPhotos) {
  const slots = getDefaultGuidePhotoSlots();
  if (!Array.isArray(urlsOrPhotos) || urlsOrPhotos.length === 0) {
    return slots.map((slot, i) => ({
      ...slot,
      id: `slot-${slot.type}-${i}-${Date.now()}`,
    }));
  }

  const first = urlsOrPhotos[0];
  const isTyped = first != null && typeof first === 'object' && (first.type || first.url !== undefined);

  if (isTyped) {
    const byType = {};
    urlsOrPhotos.forEach((item) => {
      const type = item?.type;
      const url = item?.url ?? item?.imageUrl ?? '';
      if (type && GUIDE_PHOTO_TYPES.includes(type) && url) {
        byType[type] = url;
      }
    });
    return slots.map((slot, i) => ({
      ...slot,
      id: `slot-${slot.type}-${i}-${Date.now()}`,
      existingUrl: byType[slot.type] || null,
    }));
  }

  const urls = urlsOrPhotos.map((item) =>
    (typeof item === 'string' ? item : item?.url ?? item?.imageUrl ?? '')
  );
  return slots.map((slot, i) => ({
    ...slot,
    id: `slot-${slot.type}-${i}-${Date.now()}`,
    existingUrl: urls[i] || null,
  }));
}

/** Normalize value to always 3 typed slots (for backward compat with old free-form array). */
export function normalizeGuidePhotosValue(value) {
  if (Array.isArray(value) && value.length === 3 && value.every((s) => s && GUIDE_PHOTO_TYPES.includes(s.type))) {
    return value;
  }
  if (Array.isArray(value) && value.length > 0) {
    const byType = {};
    value.forEach((item) => {
      const type = item.type || GUIDE_PHOTO_TYPES[Object.keys(byType).length] || GUIDE_PHOTO_TYPES[0];
      if (!byType[type]) byType[type] = item;
    });
    return GUIDE_PHOTO_TYPES.map((type, i) => {
      const existing = byType[type] || value[i];
      if (existing && existing.type === type) {
        return { ...existing, type };
      }
      return {
        type,
        id: existing?.id || `slot-${type}-${i}`,
        file: existing?.file ?? null,
        previewUrl: existing?.previewUrl ?? null,
        existingUrl: existing?.existingUrl ?? existing?.previewUrl ?? null,
      };
    });
  }
  return getDefaultGuidePhotoSlots();
}

/** Ensure filename ends with .jpg for backend validation. */
export function asJpgFile(file, fileName) {
  if (!file) return null;
  const name = fileName || file.name || 'photo.jpg';
  const jpgName = name.toLowerCase().endsWith('.jpg') || name.toLowerCase().endsWith('.jpeg')
    ? name.replace(/\.jpeg$/i, '.jpg')
    : `${name}.jpg`;
  if (file.name === jpgName) return file;
  return new File([file], jpgName, { type: file.type || 'image/jpeg' });
}
